import { Injectable } from '@angular/core';
import { OrderedMap } from 'immutable';

import { DxListsStorage } from '../core/dx-lists.storage';
import { DcDxGrids } from '../core/dx-types';
import { TagAdapter } from '../core/tag.adapter';
import { IProjectMemberModel } from '../requesters/core/models/project-member.model';
import { IProjectModel } from '../requesters/core/models/project.model';
import { OrphanProjectItemEntity } from './components/orphan-project-list/orphan-project-item.entity';
import {
	IOrphanProjectListExtra,
	OrphanProjectListEntity,
} from './components/orphan-project-list/orphan-project-list.entity';
import { ProjectAdminEntity } from './components/orphan-project-list/project-admin.entity';

@Injectable()
export class OrphanProjectParser {
	public constructor(
		private readonly dxListsStorage: DxListsStorage,
		private readonly tagAdapter: TagAdapter
	) {}

	public parseAdmins(
		response: ReadonlyArray<IProjectMemberModel>
	): Array<ProjectAdminEntity> {
		return response
			.filter(
				(user) => (user.enabled || user.is_group) && user.has_member_role?.own
			)
			.map((user) =>
				ProjectAdminEntity.build({
					id: user.id,
					name: user.name,
				})
			);
	}

	public getOrphanProjectList(
		response: ReadonlyArray<IProjectModel>
	): OrphanProjectListEntity {
		const list = this.generateOrphanProjectListStructure();
		const items = response.reduce<Array<OrphanProjectItemEntity>>(
			(acc, item) => {
				const temp = OrphanProjectItemEntity.build({
					id: item.id,
					label: item.metadata?.label,
					tags: this.tagAdapter.generateResponseMetadataTag(
						item.metadata?.tags ?? []
					),
				});
				acc.push(temp);
				return acc;
			},
			[]
		);

		const orderedMap = OrderedMap<string, OrphanProjectItemEntity>(
			items.map((item) => [`${item.id}`, item])
		);
		return list.setElements(orderedMap);
	}
	private generateOrphanProjectListStructure(): OrphanProjectListEntity {
		let availableCols: Array<IOrphanProjectListExtra> = [
			{
				field: 'label',
				alias: $localize`:i18n=@@orphan-project.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 0,
			},
			{
				field: 'adminId',
				alias: $localize`:i18n=@@orphan-project.list.column.admin:`,
				type: 'string',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 1,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@orphan-project.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 2,
			},
		];

		availableCols = this.dxListsStorage.synchronize<
			IOrphanProjectListExtra,
			OrphanProjectItemEntity
		>(DcDxGrids.OrphanProjectListStandalone, availableCols);
		return OrphanProjectListEntity.build(availableCols);
	}
}
